import React, { useState } from 'react';

import { useFacetsContext } from 'components/search/Facets/FacetsProvider';
import { FacetAccordion } from 'components/search/FacetAccordion/FacetAccordion';
import { isSingleSelectFacet } from 'components/search/FacetAccordion/FacetAccordion.utils';
import useMartyContext from 'hooks/useMartyContext';

export const FacetsSidebar = () => {
  const { testId } = useMartyContext();

  const {
    state: { facets, autoComplete },
    actions: { handleFacetSelect, trackFacetSearch },
    meta: { hasAutoComplete, navigationVersion, isShowMoreLess }
  } = useFacetsContext();

  // Each consumer manages its own accordion expanded state locally.
  const [expandedFacets, setExpandedFacets] = useState<Set<string>>(
    () => new Set(facets.filter(facet => facet.isExpanded).map(facet => facet.facetField))
  );

  const expandedValues = [...expandedFacets];

  return (
    <FacetAccordion.Root type="multiple" value={expandedValues} onValueChange={values => setExpandedFacets(new Set(values))} className="max-md:px-2">
      {facets.map((facet, facetIndex) => {
        const { facetField, facetFieldDisplayName, values } = facet;

        return (
          <FacetAccordion.Item key={facetField} value={facetField} data-test-id={testId('facetGroup')} facet={facet} context="desktop">
            <FacetAccordion.Trigger id={facetField} data-test-id-facet-head-name={facetFieldDisplayName}>
              <FacetAccordion.DisplayName data-test-id={testId('facetGroupHeadTestId')} />
              <FacetAccordion.SelectedValueNames data-test-id={testId('facetSelectedValues')} />
            </FacetAccordion.Trigger>

            <FacetAccordion.SeoLinks />

            <FacetAccordion.Content>
              {hasAutoComplete && <FacetAccordion.AutoComplete onChange={e => trackFacetSearch(facet, e.target.value)} />}

              <FacetAccordion.ValueList
                data-test-id={testId(`${facetFieldDisplayName}-values`)}
                autoComplete={autoComplete}
                navigationVersion={navigationVersion}
                isShowMoreLess={isShowMoreLess}
              >
                {({ facetValue }) => (
                  <FacetAccordion.Value
                    data-test-id={testId(`facet-${facetValue.name}`)}
                    facetValue={facetValue}
                    navigationVersion={navigationVersion}
                    onFacetSelect={() => {
                      const valueIndex = values.findIndex(value => value.name === facetValue.name);

                      handleFacetSelect({
                        facetGroup: facetField,
                        facetName: facetValue.name,
                        selectedFacetGroupIndex: facetIndex,
                        selectedFacetIndex: valueIndex,
                        isSelected: facetValue?.selected || false,
                        facetValue
                      });

                      // Auto-collapse single-select facets (e.g., Gender, Department) after selection
                      if (!facetValue?.selected && isSingleSelectFacet(facet) && navigationVersion !== 'v2p2') {
                        setExpandedFacets(prev => {
                          const next = new Set(prev);
                          next.delete(facetField);
                          return next;
                        });
                      }
                    }}
                  />
                )}
              </FacetAccordion.ValueList>
            </FacetAccordion.Content>
          </FacetAccordion.Item>
        );
      })}
    </FacetAccordion.Root>
  );
};
